import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Boxes,
  FlaskConical,
  Calculator,
  ExternalLink,
  Handshake,
  LayoutDashboard,
  Layers,
  LogOut,
  Menu,
  X,
  Package,
  Printer,
  ReceiptText,
  Settings,
  Store,
  Sparkles,
  Users,
  StickyNote,
  Wallet,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api, brl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/estoque", label: "Estoque", icon: Boxes, id: "estoque" },
  { to: "/plates", label: "Plates", icon: Layers, id: "plates" },
  { to: "/produtos", label: "Produtos", icon: Package, id: "produtos" },
  { to: "/desenvolvimento", label: "Desenvolvimento", icon: FlaskConical, id: "desenvolvimento" },
  { to: "/calculadora", label: "Calculadora", icon: Calculator, id: "calculadora" },
  { to: "/pedidos", label: "Pedidos", icon: ReceiptText, id: "pedidos" },
  { to: "/mural", label: "Mural de Recados", icon: StickyNote, id: "mural" },
  { to: "/clientes", label: "Clientes", icon: Users, id: "clientes" },
  { to: "/criadoras", label: "Parceiras Criadoras", icon: Sparkles, id: "criadoras" },
  { to: "/catalogo", label: "Catálogo", icon: Store, id: "catalogo", external: true },
  { to: "/parceiros", label: "Parceiros", icon: Handshake, id: "parceiros" },
  { to: "/producao", label: "Produção", icon: Printer, id: "producao" },
  { to: "/financeiro", label: "Financeiro", icon: Wallet, id: "financeiro" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, id: "relatorios" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, id: "configuracoes" },
];

// Atalhos da barra inferior; o menu completo fica no botão de menu.
const MOBILE_NAV = ["dashboard", "produtos", "producao", "pedidos"].map((id) =>
  NAV.find((n) => n.id === id)
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [receivable, setReceivable] = useState(0);

  useEffect(() => {
    api
      .get("/finance/receivables")
      .then(({ data }) => setReceivable(data.total))
      .catch(() => {});
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-60 flex-col border-r border-white/10 bg-card/60 backdrop-blur-xl md:flex">
        <div className="flex shrink-0 items-center gap-2 px-6 py-6">
          <img
            src="/brand/tr3nd-logo.jpg"
            alt="TR3ND Studio"
            data-testid="sidebar-logo"
            className="h-9 w-9 shrink-0 rounded-lg object-contain"
          />
          <span className="font-display text-lg font-semibold tracking-tight">TR3ND OS</span>
        </div>
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 pb-2"
             data-testid="sidebar-nav">
          {NAV.map(({ to, label, icon: Icon, id, external }) =>
            external ? (
              <a
                key={to}
                href={to}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-${id}`}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
              >
                <Icon className="h-[18px] w-[18px]" />
                {label}
                <ExternalLink className="ml-auto h-3 w-3 opacity-60" />
              </a>
            ) : (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`nav-${id}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                    isActive
                      ? "bg-brand/15 text-brand"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                  )
                }
              >
                <Icon className="h-[18px] w-[18px]" />
                {label}
                {id === "financeiro" && receivable > 0 && (
                  <span
                    className="ml-auto font-num text-[10px] text-amber-300"
                    data-testid="nav-receivable-hint"
                  >
                    {brl(receivable)}
                  </span>
                )}
              </NavLink>
            )
          )}
        </nav>
        <div className="shrink-0 border-t border-white/10 p-4">
          <a
            href="/catalogo"
            target="_blank"
            rel="noreferrer"
            data-testid="sidebar-catalog-link"
            className="mb-3 flex items-center gap-2 text-xs text-brand transition-colors hover:text-brand/80"
          >
            <Store className="h-3.5 w-3.5" /> Ver catálogo público
          </a>
          <p className="truncate text-sm font-medium" data-testid="sidebar-user-name">
            {user?.name}
          </p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="mt-3 flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-destructive"
          >
            <LogOut className="h-3.5 w-3.5" /> Sair
          </button>
        </div>
      </aside>

      {/* Header mobile */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-background/80 px-5 py-3.5 backdrop-blur-xl md:hidden">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="open-mobile-menu-btn"
            aria-label="Abrir menu"
            className="rounded-full border border-white/10 p-2 text-foreground transition-colors hover:border-brand/50 hover:text-brand"
          >
            <Menu className="h-4 w-4" />
          </button>
          <img
            src="/brand/tr3nd-logo.jpg"
            alt="TR3ND Studio"
            data-testid="topbar-logo"
            className="h-8 w-8 shrink-0 rounded-lg object-contain"
          />
          <span className="font-display text-base font-semibold">TR3ND OS</span>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn-mobile"
          className="rounded-full border border-white/10 p-2 text-muted-foreground"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      {/* Menu completo no celular: todas as áreas do sistema */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu">
          <button
            aria-label="Fechar menu"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            data-testid="close-mobile-menu-overlay"
          />
          <div className="absolute left-0 top-0 flex h-full w-[82%] max-w-xs flex-col border-r border-white/10 bg-card">
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-display text-base font-semibold">Menu</span>
              <button
                onClick={() => setMenuOpen(false)}
                data-testid="close-mobile-menu-btn"
                className="rounded-full border border-white/10 p-1.5 text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-6">
              {NAV.map(({ to, label, icon: Icon, id, external }) =>
                external ? (
                  <a
                    key={to}
                    href={to}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setMenuOpen(false)}
                    data-testid={`menu-${id}`}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-brand/10 hover:text-brand"
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                    <ExternalLink className="ml-auto h-3 w-3" />
                  </a>
                ) : (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`menu-${id}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                        isActive
                          ? "bg-brand/15 text-brand"
                          : "text-muted-foreground hover:bg-brand/10 hover:text-brand"
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                )
              )}
            </nav>
            <div className="border-t border-white/10 px-5 py-4">
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              <button
                onClick={onLogout}
                data-testid="menu-logout-btn"
                className="mt-2 flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-destructive"
              >
                <LogOut className="h-3.5 w-3.5" /> Sair
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="px-5 pb-28 pt-6 md:ml-60 md:px-8 md:pb-12 md:pt-10">
        <div className="mx-auto max-w-6xl">
          <Outlet />
        </div>
      </main>

      {/* Bottom tabs mobile */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex border-t border-white/10 bg-card/95 backdrop-blur-xl md:hidden">
        {MOBILE_NAV.map(({ to, label, icon: Icon, id }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={`tab-${id}`}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center gap-1 py-2.5 text-center text-[9px] leading-tight transition-colors",
                isActive ? "text-brand" : "text-muted-foreground"
              )
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
